<i18n>
{
  "en": {
    "createRecommendationTitle": "Create product recommendation",
    "createRecommendationSubtitle": "Create recommendations to be displayed on the menu next to products.",
    "editRecommendationTitle": "Edit product recommendation",
    "editRecommendationSubtitle": "Edit the recommendation to be displayed next to the product.",
    "mainProduct": "Main product of product recommendation",
    "subordinateProducts": "Products to recommend with main product",
    "tooLongText": "Input text is too long",
    "emptyFieldError": "Required field",
    "selectLanguage": "Language",
    "recommendedTextHint": "Text of the recommendation",
    "recommendedText": "Text of the recommendation",
    "en": "English",
    "es": "Spanish",
    "confirmCreateTitle": "Are you sure you want to create the Product Recommendation?",
    "confirmEditTitle": "Are you sure you want to edit the Product Recommendation?",
    "confirmText": "Las recomendaciones se aplicarán a los menús.",
    "confirm": "Confirm"
  },
  "es": {
    "createRecommendationTitle": "Crea una recomendación de producto",
    "createRecommendationSubtitle": "Crea recomendaciones para que se muestren en el menú al lado de los productos.",
    "editRecommendationTitle": "Edita una recomendación de producto",
    "editRecommendationSubtitle": "Edita la recomendación que se va a mostrar junto al producto.",
    "mainProduct": "Producto principal de la recomendación",
    "subordinateProducts": "Productos a recomendar con el principal",
    "tooLongText": "El texto es demasiado largo",
    "emptyFieldError": "Campo obligatorio",
    "selectLanguage": "Idioma",
    "recommendedTextHint": "Texto de la recomendación",
    "recommendedText": "Texto de la recomendación",
    "en": "Inglés",
    "es": "Español",
    "confirmCreateTitle": "¿Está seguro de que quiere crear la recomendación de producto?",
    "confirmEditTitle": "¿Está seguro de que quiere editar la recomendación de producto?",
    "confirmText": "Las recomendaciones se aplicarán a los menús.",
    "confirm": "Confirmar"
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if="visible" height="100%" class="px-7 py-5">
      <div v-if="!editMode" class="d-flex flex-column">
        <v-card-title class="word-break pa-0 mb-4">{{ $t('createRecommendationTitle') }}</v-card-title>
        <v-card-subtitle class="pa-0">{{ $t('createRecommendationSubtitle') }}</v-card-subtitle>
      </div>
      <div v-else class="d-flex flex-column">
        <v-card-title class="word-break pa-0 mb-4">{{ $t('editRecommendationTitle') }}</v-card-title>
        <v-card-subtitle class="pa-0">{{ $t('editRecommendationSubtitle') }}</v-card-subtitle>
      </div>
      <v-container class="pa-0 mt-3 mb-3">
        <v-form v-model="valid" class="d-flex flex-column align-left" ref="form">
          <DynameatLangugageFieldGenerator
            v-model="recommendedText"
            :label="$t('recommendedText')"
            :type="'textArea'"
            :rules="shortTextRulesOrEmpty"
            required
          />
          <dynameat-autocomplete v-model="mainProduct" :items="productList" :disabled="editMode"
                                 :rules="objectRules" :filter="onProductTextFilter"
                                 :label="$t('mainProduct')" prepend-icon="mdi-database-search"
                                 item-text="name" item-value="guid" chips persistent-hint
                                 return-object :required="true">
            <template v-slot:item="data">
              <template>
                <v-list-item-avatar>
                  <v-img :src="data.item.image" :alt="data.item.name" :lazy-src="defaultProductImg" contain/>
                </v-list-item-avatar>
                <v-list-item-content class="h-scroll">
                  <v-list-item-title class="no-wrap">
                    {{ data.item.commercial_name }}
                    (
                    {{currency.hasLeftPosition() ? currency.symbol : ''}}
                    {{ data.item.first_price }}
                    {{currency.hasRightPosition() ? currency.symbol : ''}}
                    )
                  </v-list-item-title>
                  <v-list-item-subtitle class="no-wrap">{{ data.item.name }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </template>
            <template v-slot:selection="data">
              <v-chip class="caption | d-flex justify-center align-center"
                      :disabled="editMode" :close="!editMode" @click:close="onRemoveMainProduct"
                      @click.stop>
                <div class="chip-ellipsis">{{ data.item.name }}</div>
              </v-chip>
            </template>
          </dynameat-autocomplete>
          <dynameat-autocomplete v-model="subordinatesProducts" :items="productList"
                                 :rules="objectRules" :filter="productTextFilter"
                                 :label="$t('subordinateProducts')"
                                 prepend-icon="mdi-database-search"
                                 item-text="name" item-value="guid" chips persistent-hint
                                 return-object :required="true" multiple>
            <template v-slot:item="data">
              <template>
                <v-list-item-avatar>
                  <v-img :src="data.item.image" :alt="data.item.name" :lazy-src="defaultProductImg" contain/>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ data.item.commercial_name }}
                    (
                    {{currency.hasLeftPosition() ? currency.symbol : ''}}
                    {{ data.item.first_price }}
                    {{currency.hasRightPosition() ? currency.symbol : ''}}
                    )
                  </v-list-item-title>
                  <v-list-item-subtitle>{{ data.item.name }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </template>
            <template v-slot:selection="data">
              <v-chip class="caption | d-flex justify-center align-center" close
                      @click:close="onRemoveRecommendedProduct(data.index)" @click.stop>
                <div class="chip-ellipsis">{{ data.item.name }}</div>
              </v-chip>
            </template>
          </dynameat-autocomplete>
        </v-form>
      </v-container>
      <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm" :confirmDisabled="!formValid"
                                  :confirmText="$t('confirm')"/>
    </v-card>
    <dynameat-confirmation-modal :visible="confirmModalVisible" :title="confirmationTitle"
                                 :description="$t('confirmText')" :confirm-text="$t('confirm')"
                                 @confirm="onConfirmConfirmModal" @cancel="onCloseConfirmModal"/>
  </v-dialog>
</template>

<script>

import { maxChars, notEmptyObject, notEmptyString, } from '@core/helpers/formValidators';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapGetters } from '@core/helpers/decorators';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { MODEL_INSTANCES_LANGUAGES } from '@core/constants';
import DynameatAutocomplete from '@dynameat/components/DynameatAutocomplete';
import { productTextFilter } from '@core/mixins';
import DynameatLangugageFieldGenerator from '@dynameat/components/DynameatLanguageFieldGenerator';
import { ProductRecommendation } from '../models';

@Component
@Components({
  DynameatConfirmationModal, DynameatModalButtons, DynameatAutocomplete, DynameatLangugageFieldGenerator
})
@MapGetters(['pointOfSaleLanguages', 'currency'])
export default class ProductRecommendationCreateEditModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  @Prop({ type: Array, default: () => [] }) productList;

  @Prop({ type: Object, default: null }) productRecommendation;

  @Prop({ type: Boolean, default: false }) editMode;

  valid = false;

  confirmModalVisible = false;

  mainProduct = null;

  subordinatesProducts = []

  selectedLanguage = null;

  recommendedText = {};

  mainLanguage = MODEL_INSTANCES_LANGUAGES[0];

  async created() {
    this.clearOwnData();
  }

  get objectRules() {
    return [notEmptyObject(this.$t('emptyFieldError'))];
  }

  get shortTextRulesOrEmpty() {
    return {
      alt1: [maxChars(this.$t('tooLongText'), 128), notEmptyString(this.$t('emptyFieldError'))],
      alt2: [maxChars(this.$t('tooLongText'), 128)],
      alt3: [maxChars(this.$t('tooLongText'), 128)]
    };
  }

  get formValid() {
    return this.valid;
  }

  get availableLanguages() {
    return this.pointOfSaleLanguages;
  }

  get defaultProductImg() {
    // eslint-disable-next-line global-require
    return require('@dynameat/assets/images/dynameat-logo-secondary.png');
  }

  get confirmationTitle() {
    return (!this.editMode) ? this.$t('confirmCreateTitle') : this.$t('confirmEditTitle');
  }

  onConfirm() {
    if (this.formValid) {
      this.onOpenConfirmModal();
    }
  }

  onCancel() {
    this.clearOwnData();
    this.$emit('cancel');
  }

  onProductTextFilter = productTextFilter

  clearOwnData() {
    this.valid = false;
    this.confirmModalVisible = false;
    this.mainProduct = null;
    this.subordinatesProducts = [];
    this.selectedLanguage = null;
    this.recommendedText = {};
  }

  onOpenConfirmModal() {
    this.confirmModalVisible = true;
  }

  onCloseConfirmModal() {
    this.confirmModalVisible = false;
  }

  async onConfirmConfirmModal() {
    this.confirmModalVisible = false;
    if (!this.editMode) {
      this.$emit('confirm-create', this.buildProductRecommendation());
    } else {
      this.$emit('confirm-edit', this.buildProductRecommendation());
    }
    this.clearOwnData();
  }

  buildProductRecommendation() {
    const productRecommendationLanguages = {};
    this.availableLanguages.forEach((el) => {
      productRecommendationLanguages[`text_${el.alternative}`] = this.recommendedText[el.alternative];
    });

    return ProductRecommendation.toInstance({
      guid: this.productRecommendation?.guid || null,
      parent_product: this.mainProduct.guid,
      child_products: this.subordinatesProducts.map((product) => product.guid),
      ...productRecommendationLanguages
    });
  }

  setProducts() {
    this.mainProduct = this.productList.find((product) => product.guid === this.productRecommendation.parentProduct);
    this.subordinatesProducts = this.productList.filter((product) => this.productRecommendation.childProducts.includes(product.guid));
    this.availableLanguages.forEach((el) => {
      this.recommendedText[el.alternative] = this.productRecommendation[`text_${el.alternative}`];
    });
  }

  onRemoveRecommendedProduct(index) {
    this.subordinatesProducts.splice(index, 1);
  }

  onRemoveMainProduct() {
    this.mainProduct = null;
  }

  @Watch('visible')
  onVisible() {
    this.clearOwnData();
    if (this.visible) {
      this.selectedLanguage = { text: this.$t(this.mainLanguage), value: this.mainLanguage };
      if (this.editMode) {
        this.setProducts();
      }
    }
  }

  @Watch('productList')
  onProductList() {
    if (this.visible && this.editMode) this.setProducts();
  }
}
</script>
<style scoped>
.word-break {
  word-break: break-word;
}

.chip-ellipsis {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.h-scroll {
  overflow-y: hidden;
  overflow-x: auto;
}

.no-wrap {
  text-overflow: initial;
  overflow: initial;
  white-space: nowrap;
}
</style>
